<template>
  <section id="packs">
    <h2 class="sr-only">Icon Packs</h2>
    <div
      class="row roomy laptop:align-middle margin-bottom-xl"
      style="
        --button-background: transparent;
        --button-hover-background: transparent;
        --button-hover-color: var(--fa-dk-blue);
        --button-margin-bottom: 0;
        --link-color: var(--fa-navy);
      "
    >
      <div
        class="column-12 laptop:column-9 display-flex flex-items-center flex-wrap margin-bottom-md laptop:margin-bottom-0"
        style="--button-border-radius: var(--border-radius-pill); gap: var(--spacing-sm)"
      >
        <h3 class="sr-only">Filter Icon Packs by Icon Types</h3>
        <button
          v-for="(tag, index) in visualTags"
          :key="index"
          type="button"
          class="size-sm padding-y-sm action-scale packs-type-filter-button"
          :style="{
            '--button-box-shadow': 'none',
            '--button-box-shadow-width': '0',
            '--button-border-color': isIconVisualTagSelected(tag.label) ? 'var(--fa-dk-blue)' : 'var(--fa-gravy)',
            '--button-color': isIconVisualTagSelected(tag.label) ? 'var(--fa-dk-blue)' : 'initial'
          }"
          @click="toggleIconVisualTag(tag.label)"
        >
          <i :class="`${tag.icon} fa-lg fa-fw margin-right-3xs`"></i>
          {{ tag.label }}
        </button>
      </div>
      <div class="column-12 laptop:column-3 display-flex flex-items-center laptop:flex-content-end">
        <h3 class="sr-only">Sort Icon Packs</h3>

        <button
          v-for="(option, index) in sortOptions"
          :key="index"
          type="button"
          :aria-label="option.label"
          class="size-sm flat compact action-scale"
          :style="{
            '--button-border-color': isSortOptionActive(option.label) ? 'var(--fa-dk-blue)' : 'transparent',
            '--button-color': isSortOptionActive(option.label) ? 'var(--fa-dk-blue)' : 'initial'
          }"
          data-balloon-pos="up"
          @click="setSortOption(option.label)"
        >
          <font-awesome-icon
            :icon="$v6[option.iconStyle || 'solid'][option.iconName]"
            fixed-width
            size="lg"
          />
          <span class="sr-only">{{ option.label }}</span>
        </button>
      </div>
    </div>

    <transition-group
      name="pack-transition"
      tag="div"
      class="packs-listing"
    >
      <pack-card
        v-for="(pack, index) in filteredIconPacks"
        :key="pack.name"
        :icon-pack="pack"
        :family-styles="pack.filteredFamilyStyles"
        :is-last="filteredIconPacks.length === index + 1"
      />
    </transition-group>

    <article
      class="card muted roomy margin-top-2xl"
      style="--card-background: transparent; --card-border-color: var(--fa-gravy); --card-border-width: var(--border-width-sm); --card-border-style: dashed"
    >
      <wa-question-mark-block
        block-color="var(--text-color)"
        block-hover-primary-color="var(--fa-navy)"
        block-size="3x"
      />
      <p class="margin-top-md">New Icon Packs Coming Soon!</p>
    </article>
  </section>
</template>

<script>
  import { mapActions } from 'vuex'
  import { getIconPacks } from 'app/views/search/collections.js'
  import { kebabCase } from 'lodash'
  import PackCard from './PackCard'
  import IconsLandingData from '../IconsLandingData.json'
  import collection_data_v5 from 'app/views/search/collections-v5.json'
  import collection_data_v6 from 'app/views/search/collections-v6.json'

  export default {
    name: 'IconsPacks',

    components: {
      PackCard
    },

    data() {
      return {
        iconPacks: [],
        isBlockBouncing: false,
        packsFeaturedOrder: IconsLandingData.packsFeaturedOrder,
        selectedIconVisualTags: [],
        selectedSortOption: 'featured',
        sortOptions: IconsLandingData.sortOptions
      }
    },

    computed: {
      collectionData() {
        return this.version === 5 ? collection_data_v5 : collection_data_v6
      },

      filteredIconPacks() {
        return this.iconPacks
          .map((pack) => ({
            ...pack,
            filteredFamilyStyles: this.getFilteredFamilyStyles(pack)
          }))
          .filter((pack) => pack.filteredFamilyStyles.length > 0)
      },

      version() {
        return this.$releases.active.versionParts.major
      },

      visualTags() {
        return this.collectionData['visual-tags'].map((tag) => ({
          label: tag.label,
          icon: tag.icon
        }))
      }
    },

    created() {
      this.iconPacks = getIconPacks(this.version)
        .filter((pack) => pack.status === 'released')
        .map((pack) => ({
          ...pack,
          lastUpdatedVersion: pack.lastUpdatedVersion || '0.0.0'
        }))

      this.setSortOption('Featured')
    },

    async mounted() {
      this.$posthog.capture('icons_landing:packs_tab_view')

      await this.fetchPopularIcons()
    },

    methods: {
      ...mapActions('iconPopularityList', ['fetchPopularIcons']),

      // helper function to compare semantic versions
      compareVersions(versionA, versionB) {
        const parseVersion = (version) => version.split('.').map(Number)

        const [majorA, minorA, patchA] = parseVersion(versionA)
        const [majorB, minorB, patchB] = parseVersion(versionB)

        if (majorA !== majorB) return majorA - majorB
        if (minorA !== minorB) return minorA - minorB
        return patchA - patchB
      },

      getFilteredFamilyStyles(pack) {
        if (this.selectedIconVisualTags.length === 0) return pack.familyStyles

        return pack.familyStyles.filter((style) => style.visualTags.some((tag) => this.selectedIconVisualTags.includes(tag)))
      },

      isIconVisualTagSelected(label) {
        const slug = kebabCase(label)
        return this.selectedIconVisualTags.includes(slug)
      },

      isSortOptionActive(label) {
        const slug = kebabCase(label)
        return this.selectedSortOption === slug
      },

      setSortOption(selectedLabel) {
        const slug = kebabCase(selectedLabel)
        this.selectedSortOption = slug

        switch (slug) {
          case 'alphabetically':
            this.iconPacks.sort((a, b) => a.label.localeCompare(b.label))
            break
          case 'release-date':
            this.iconPacks.sort((a, b) => this.compareVersions(b.lastUpdatedVersion, a.lastUpdatedVersion))
            break
          case 'featured':
            this.iconPacks.sort((a, b) => {
              const indexA = this.packsFeaturedOrder.indexOf(a.name)
              const indexB = this.packsFeaturedOrder.indexOf(b.name)

              return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
            })
            break
          default:
            break
        }
      },

      toggleIconVisualTag(label) {
        const slug = kebabCase(label)
        const index = this.selectedIconVisualTags.indexOf(slug)
        if (index === -1) {
          this.selectedIconVisualTags.push(slug)
        } else {
          this.selectedIconVisualTags.splice(index, 1)
        }
      }
    }
  }
</script>
